import React, { useCallback, useRef, useState } from 'react';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { isNil } from 'ramda';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { NodeProps } from 'reactflow';
import { animated, useSpring } from 'react-spring';
import { actionsSelector } from '~/components/page/Automation/v2/state/actions';
import interactions, {
  copySelectableIds,
  copyRemovableIds,
} from '~/components/page/Automation/v2/state/interactions';
import focusedActionId from '~/components/page/Automation/v2/state/focusedActionId';
import { DEFAULT_WIDTH } from '../../constants';
import Icon from '~/components/atom/Icon';
import Button from '~/components/atom/Button';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body, Heading4, Variant } from '~/components/atom/Typography';
import useBuilderContext from '../../../../hooks/useBuilderContext';
import getIssueLevel from '../../../../utils/getIssueLevel';
import { getCardValues } from './utils/getCardValues';
import type { ClientFlowAction } from '~/graphql/types.client';
import TEST_ID from './index.testid';
import isEmptyAction from '../../../../utils/isEmptyAction';
import DropdownListContainer from '~/components/molecule/Dropdown/components/DropdownListContainer';
import ActionLabel from '~/components/page/Automation/v2/components/Builder/components/RepresentationLabel/components/ActionLabel';
import getCardLabel from './utils/getCardLabel';
import onConnectActions from './utils/onConnectActions';
import useGetCardOptions from './hooks/useGetCardOptions';
import useIssuesByActionId from '../../../../hooks/useIssuesByActionId';
import IconOnlyButton from '~/components/organism/Selector/components/IconOnlyButton';
import isDevModeEnabledState from '~/components/organism/NavigationFrame/components/Top/components/DevTools/state';

export type NodeData<ActionT> = {
  id: string;
  action: ActionT;
};

export type Props<ActionT extends ClientFlowAction> = NodeProps<
  NodeData<ActionT>
> & {
  dataTestId?: string;
  actionType: ClientFlowAction['actionType'];
  heading: string;
  disabled?: boolean;
  children?: React.ReactNode;
};

const CardTemplate: React.FCC<Props<ClientFlowAction>> = ({
  actionType,
  heading,
  disabled = false,
  selected = false,
  children,
  data,
  dataTestId,
}) => {
  const isDevModeEnabled = useRecoilValue(isDevModeEnabledState);

  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const { accountId, flowBlueprintId } = useBuilderContext();

  const storedIssues = useIssuesByActionId(data.action.id);
  const setActions = useSetRecoilState(
    actionsSelector({
      accountId,
      flowBlueprintId,
    }),
  );
  const [currentInteraction, setInteraction] = useRecoilState(interactions);
  const [shouldHighlight, setShouldHighlight] = useState(false);
  const deepLinkedFocusedActionId = useRecoilValue(focusedActionId);
  const isfocused = deepLinkedFocusedActionId === data.action.id;

  const dotMenuRef = useRef<HTMLButtonElement | null>(null);

  const styles = useSpring({
    from: {
      transform: 'scale(0)',
      opacity: 0,
    },
    to: {
      transform: 'scale(1)',
      opacity: 1,
    },
    config: {
      mass: 1,
      tension: 170,
      friction: 13,
    },
  });

  const theme = useTheme();
  const issueLevel = getIssueLevel(storedIssues);
  const { icon, color } = getCardValues({ actionType, issueLevel, theme });

  const dropdownOptions = useGetCardOptions({
    action: data.action,
  });

  const isConnectMode = currentInteraction?.type === 'connectAction';
  const isHighlightMode = currentInteraction?.type === 'highlightActions';
  const isCopyMode = currentInteraction?.type === 'copyAction';

  const isDisabledWhenConnecting =
    isConnectMode &&
    isNil(
      currentInteraction.validTargets.find(({ id }) => id === data.action.id),
    );

  const enableHighlightOnMouseOver = isConnectMode;
  const highlighted = isHighlightMode
    ? currentInteraction.highlightedActions?.includes(data.action.id)
    : true;

  /** Copy mode states */

  const selectableIds = useRecoilValue(copySelectableIds);
  const removableIds = useRecoilValue(copyRemovableIds);

  const isSelectingPhase =
    isCopyMode && currentInteraction.phase === 'selecting';
  const isBeingCopied =
    isCopyMode &&
    currentInteraction.copiedActions.some(a => a.id === data.action.id);
  const isSelectableNeighbor =
    isSelectingPhase && selectableIds.has(data.action.id);
  const isRemovableFromCopy =
    isSelectingPhase && isBeingCopied && removableIds.has(data.action.id);
  const isDisabledWhenCopying =
    isCopyMode && !isBeingCopied && !isSelectableNeighbor;
  const isPastingOrChoosingPathToConnect =
    isCopyMode &&
    (currentInteraction.phase === 'pasting' ||
      currentInteraction.phase === 'choosingPathToConnect');

  /** Disabled states */

  const optionsDisabled =
    !highlighted ||
    isDisabledWhenConnecting ||
    isCopyMode ||
    enableHighlightOnMouseOver;

  const isCardDisabled =
    disabled ||
    !highlighted ||
    isDisabledWhenConnecting ||
    isDisabledWhenCopying ||
    isPastingOrChoosingPathToConnect;

  const actionLabel = data.action.label
    ? getCardLabel(data.action.label)
    : null;

  const toggleCopySelection = useCallback(() => {
    if (currentInteraction?.type !== 'copyAction') return;

    const isAlreadySelected = currentInteraction.copiedActions.some(
      a => a.id === data.action.id,
    );

    const updatedBranch = isAlreadySelected
      ? currentInteraction.copiedActions.filter(a => a.id !== data.action.id)
      : [...currentInteraction.copiedActions, data.action];

    if (updatedBranch.length === 0) {
      setInteraction(null);
      return;
    }

    setInteraction({
      ...currentInteraction,
      copiedActions: updatedBranch,
    });
  }, [currentInteraction, data.action, setInteraction]);

  const onCardClick = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      if (isCopyMode) {
        if (isSelectingPhase && (isRemovableFromCopy || isSelectableNeighbor)) {
          toggleCopySelection();
        }
        return;
      }

      switch (currentInteraction?.type) {
        case 'connectAction': {
          setActions(prevActions => {
            const nextActions = onConnectActions({
              actions: prevActions,
              currentInteraction,
              targetAction: data.action,
            });

            return nextActions;
          });

          setShouldHighlight(false);
          return setInteraction(null);
        }
        case null:
        default:
          return setInteraction({
            type: 'updateAction',
            action: data.action,
          });
      }
    },
    [
      currentInteraction,
      setInteraction,
      data.action,
      setActions,
      isCopyMode,
      isSelectingPhase,
      isRemovableFromCopy,
      isSelectableNeighbor,
      toggleCopySelection,
    ],
  );

  return (
    <>
      <Container
        onClick={onCardClick}
        data-testid={dataTestId}
        $color={color}
        $selected={selected || showDropdown || shouldHighlight}
        $disabled={isCardDisabled}
        $copiedBorder={isBeingCopied}
        $copiedLocked={isBeingCopied && !isRemovableFromCopy}
        $selectableNeighbor={isSelectableNeighbor}
        $hasIssue={!isNil(issueLevel)}
        $focused={isfocused}
        style={styles}
        onMouseEnter={
          enableHighlightOnMouseOver
            ? () => setShouldHighlight(true)
            : undefined
        }
        onMouseLeave={
          enableHighlightOnMouseOver
            ? () => setShouldHighlight(false)
            : undefined
        }
        data-hasissue={!isNil(issueLevel)}
      >
        {isDevModeEnabled && <>{data.action.id}</>}
        {dropdownOptions.length !== 0 && (
          <Options>
            <StyledMenuIcon
              dataTestId={TEST_ID.OPTIONS}
              name="dot-menu"
              disabled={disabled || optionsDisabled}
              onClick={e => {
                e.stopPropagation();

                setShowDropdown(prev => !prev);
              }}
              ref={dotMenuRef}
            />
            <DropdownListContainer
              dataTestId={TEST_ID.DROPDOWN}
              onChange={v => {
                v.option.payload(data.action);
                setShowDropdown(false);
              }}
              dropdownListOpen={showDropdown}
              onClickOutside={() => setShowDropdown(false)}
              onClose={() => setShowDropdown(false)}
              options={dropdownOptions}
              openerRef={dotMenuRef}
            />
          </Options>
        )}
        <JustificationContainer
          justification="start"
          align="center"
          nonResponsive={true}
          margin={children ? [null, null, 'm', null] : [null, null]}
        >
          <StyledIcon
            name={icon}
            $iconColor={color}
            $issue={!isNil(issueLevel)}
          />
          {actionLabel && <ActionLabel label={actionLabel} />}
          <Heading4 variant={Variant.primary} margin={[null]}>
            {heading}
          </Heading4>
        </JustificationContainer>
        <ChildrenWrapper>
          {isEmptyAction(data.action) ? (
            <>
              <Body>Deze actie is nog niet gedefineerd</Body>
              <Button
                onClick={onCardClick}
                label="Defineer deze actie"
                appearance="secondary"
              />
            </>
          ) : (
            children
          )}
        </ChildrenWrapper>
      </Container>
    </>
  );
};

const marchingAnts = keyframes`
  from {
    background-position: 0 0, 0 100%, 0 0, 100% 0;
  }
  to {
    background-position: 16px 0, -16px 100%, 0 -16px, 100% 16px;
  }
`;

const Container = styled(animated.div)<{
  $color: string;
  $selected?: boolean;
  $disabled?: boolean;
  $hasIssue?: boolean;
  $focused?: boolean;
  $copiedBorder: boolean;
  $copiedLocked?: boolean;
  $selectableNeighbor?: boolean;
}>(
  ({
    theme,
    $color,
    $selected,
    $disabled,
    $hasIssue,
    $focused,
    $copiedBorder,
    $copiedLocked,
    $selectableNeighbor,
  }) => css`
    border-radius: ${theme.getTokens().border.radius.m};
    background: ${theme.color('white')};
    outline: ${!$disabled && $selected
      ? `2px solid ${$color}`
      : `1px solid transparent`};
    border-left: ${theme.spacing('xxs')} solid ${$color};
    width: ${DEFAULT_WIDTH}px;
    padding: ${theme.spacing('l')};
    cursor: pointer;
    pointer-events: ${$disabled ? 'none' : 'auto'};
    box-shadow: ${$focused
      ? `0 0 0 3px ${theme.color('primary')}, 0 0 14px 0 ${theme.color(
          'primary',
        )}`
      : $hasIssue
        ? `0 0 10px 0 ${$color}`
        : 'unset'};
    user-select: none;
    opacity: ${$disabled && 0.7} !important;
    position: relative;

    transition:
      outline 0.2s ${$selected ? 'ease-out' : 'ease-in'},
      opacity 0.2s ease-out,
      box-shadow 0.3s ease-out;

    ${$copiedBorder &&
    !$copiedLocked &&
    css`
      outline: 3px solid ${theme.color('primary', 'light')};

      &:hover {
        outline: 3px dashed ${theme.color('primary', 'light')};
      }
    `}

    ${$copiedLocked &&
    css`
      outline: 3px solid ${theme.color('primary', 'light')};
      cursor: not-allowed;
    `}

    ${$selectableNeighbor &&
    css`
      outline: 3px solid transparent;
      opacity: 0.85;

      &::before {
        content: '';
        position: absolute;
        top: -3px;
        right: -3px;
        bottom: -3px;
        left: calc(-3px - ${theme.spacing('xxs')});
        border-radius: calc(${theme.getTokens().border.radius.m} + 5px);
        pointer-events: none;
        z-index: ${theme.getTokens().zIndex.minimum};
        opacity: 1;
        transition: opacity 0.2s ease-out;
        background:
          linear-gradient(
              90deg,
              ${theme.color('accent', 'base')} 50%,
              transparent 0
            )
            0 0 / 16px 3px repeat-x,
          linear-gradient(
              90deg,
              ${theme.color('accent', 'base')} 50%,
              transparent 0
            )
            0 100% / 16px 3px repeat-x,
          linear-gradient(
              0deg,
              ${theme.color('accent', 'base')} 50%,
              transparent 0
            )
            0 0 / 3px 16px repeat-y,
          linear-gradient(
              0deg,
              ${theme.color('accent', 'base')} 50%,
              transparent 0
            )
            100% 0 / 3px 16px repeat-y;
        animation: ${marchingAnts} 0.7s linear infinite;
      }

      @media (prefers-reduced-motion: reduce) {
        &::before {
          animation: none;
        }
      }

      &:hover {
        opacity: 1;
        outline: 3px solid ${theme.color('primary', 'base')};
      }

      &:hover::before {
        opacity: 0;
      }
    `}
  `,
);

const StyledIcon = styled(Icon)<{ $issue: boolean; $iconColor: string }>(
  ({ theme, $issue, $iconColor }) => css`
    padding-right: ${theme.spacing('l')};
    font-size: ${theme.fontSize('xl')};
    color: ${$issue && $iconColor};
  `,
);
const StyledMenuIcon = styled(IconOnlyButton)(
  ({ theme }) => css`
    color: ${theme.color('primary', 'light')};
  `,
);

const Options = styled.div<{}>(
  ({ theme }) => css`
    position: absolute;
    top: ${theme.space('m')};
    right: ${theme.space('m')};
  `,
);

const ChildrenWrapper = styled.div<{}>(
  ({ theme }) => css`
    margin-left: ${theme.spacing('xxxl')};
    margin-right: ${theme.spacing('xxs')};
  `,
);

export const handleStyle = { opacity: '0', height: '1rem', left: '50%' };

export default CardTemplate;
